/**
 * Embed Layout
 * 
 * Wrapper component for all pages that can be embedded via iframe.
 * Handles loading states, errors, and provides consistent UI.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEmbedAuth } from '@/shared/contexts/EmbedAuthContext';
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';

export default function EmbedLayout({ children }) {
    const { t } = useTranslation(['common']);
    const { isIframe, isAuthenticated, isLoading, error, requestTokenRefresh } = useEmbedAuth();

    // Not in iframe
    if (!isIframe) {
        return (
            <div style={{
                width: '100%',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F8F9FA',
                padding: '20px',
            }}>
                <AlertTriangle size={48} color="#D97706" style={{ marginBottom: '16px' }} />
                <p style={{ fontSize: '18px', color: '#374151', fontWeight: 600, marginBottom: '8px' }}>
                    {t('common:embed.notEmbedded')}
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>
                    {t('common:embed.notEmbeddedDesc')}
                </p>
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div style={{
                width: '100%',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F8F9FA',
            }}>
                <Loader2 size={40} color="#3B82F6" style={{ animation: 'spin 1s linear infinite' }} />
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                <p style={{ marginTop: '16px', color: '#666666', fontSize: '14px' }}>
                    {t('common:embed.initializing')}
                </p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div style={{
                width: '100%',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F8F9FA',
                padding: '20px',
            }}>
                <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    backgroundColor: '#FEE2E2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                }}>
                    <AlertTriangle size={32} color="#DC2626" />
                </div>
                <p style={{ fontSize: '18px', color: '#374151', fontWeight: 600, marginBottom: '8px' }}>
                    {t('common:embed.authFailed')}
                </p>
                <p style={{ fontSize: '14px', color: '#6B7280', textAlign: 'center', marginBottom: '20px', maxWidth: '400px' }}>
                    {error}
                </p>
                <button
                    onClick={requestTokenRefresh}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        backgroundColor: '#111827',
                        color: '#FFFFFF',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    <RefreshCw size={16} />
                    {t('common:embed.retry')}
                </button>
            </div>
        );
    }

    // Not authenticated
    if (!isAuthenticated) {
        return (
            <div style={{
                width: '100%',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F8F9FA',
            }}>
                <Loader2 size={40} color="#3B82F6" style={{ animation: 'spin 1s linear infinite' }} />
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                <p style={{ marginTop: '16px', color: '#666666', fontSize: '14px' }}>
                    {t('common:embed.waitingAuth')}
                </p>
            </div>
        );
    }

    // Authenticated - render children
    return <>{children}</>;
}
